import React from "react";
import VariantPanel, { type VariantFact } from "@/components/common/VariantPanel";
import { useLanguage } from "@/hooks/useLanguage";
import { pick, type Lang } from "@/lib/i18n";
interface NozzlesCopy {
  eyebrow: string;
  title: string;
  lead: string;
  points: string[];
  facts: VariantFact[];
  imageAlt: string;
}
const copyData: Record<Lang, NozzlesCopy> = {
  en: {
    eyebrow: "Precision Fluid Control & Retention",
    title: "Filter Nozzles & Vessel Strainers",
    lead: "Engineered stainless steel wedge-profile and wire mesh filter nozzles designed for uniform liquid collection, low pressure drop, and non-clogging performance in industrial vessels.",
    points: ["Continuous V-wire slot profile creates two-point contact, preventing particulate wedging and facilitating backwash.", "All-welded fusion construction withstands cyclic water hammer loads and thermal fluctuations without media loss.", "Configurable with standard BSP/NPT threaded shanks, expanding sleeves, or custom collars to match vessel plate thickness."],
    facts: [{
      label: "Slot Aperture",
      value: "0.05 – 2.0 mm"
    }, {
      label: "Standard Alloys",
      value: "AISI 304 / 316L"
    }, {
      label: "Thread Fittings",
      value: '1/2" – 2" BSP/NPT'
    }, {
      label: "Flow Direction",
      value: "Direct & Reverse"
    }],
    imageAlt: "MutualFilter precision stainless steel filter nozzles and strainers"
  },
  zh: {
    eyebrow: "精密流体控制与介质截留",
    title: "滤水帽与工业喷嘴组件",
    lead: "工程级不锈钢楔形丝与金属丝网滤水帽及喷嘴，专为工业反应器与水处理容器提供均匀液体收集、低压降与防堵塞性能。",
    points: ["连续 V 型缝隙两点线接触截面，杜绝颗粒介质卡嵌，实现快速反冲洗自洁。", "全熔阻焊坚固结构，耐受周期性水锤冲击与温度波动，确保滤料介质零泄漏。", "提供标准 BSP/NPT 螺纹管柄、膨胀套管及定制法兰衬套，精确适配各类容器滤板厚度。"],
    facts: [{
      label: "缝隙孔径",
      value: "0.05 – 2.0 mm"
    }, {
      label: "标准材质",
      value: "AISI 304 / 316L"
    }, {
      label: "螺纹标准",
      value: '1/2" – 2" BSP/NPT'
    }, {
      label: "流向形式",
      value: "正向与反向流动"
    }],
    imageAlt: "MutualFilter 精密不锈钢滤水帽与滤喷嘴"
  },
  es: {
    eyebrow: "Control preciso de fluidos y retención de medios",
    title: "Boquillas y toberas de filtración",
    lead: "Boquillas de filtración en perfiles de alambre en cuña y malla de acero inoxidable diseñadas para una recolección uniforme, baja pérdida de carga y resistencia a la colmatación en recipientes industriales.",
    points: ["El perfil de ranura continua en V crea un contacto en dos puntos que evita el acuñamiento y facilita el retrolavado.", "Construcción totalmente soldada por fusión que resiste golpes de ariete cíclicos y variaciones térmicas sin pérdida de medio.", "Configurables con vástagos roscados estándar BSP/NPT, manguitos expansibles o cuellos a medida para cualquier espesor de placa."],
    facts: [{
      label: "Abertura de ranura",
      value: "0,05 – 2,0 mm"
    }, {
      label: "Aleaciones",
      value: "AISI 304 / 316L"
    }, {
      label: "Roscas",
      value: '1/2" – 2" BSP/NPT'
    }, {
      label: "Flujo",
      value: "Directo e inverso"
    }],
    imageAlt: "Boquillas y toberas de filtración de acero inoxidable de precisión MutualFilter"
  },
  de: {
    eyebrow: "Präzisions-Flüssigkeitskontrolle & Medienrückhaltung",
    title: "Filterdüsen & Düsenboden-Elemente",
    lead: "Präzisions-Filterdüsen aus Edelstahl mit Spaltrohr- und Gewebeprofilen für gleichmäßige Flüssigkeitssammlung, geringen Druckverlust und verstopfungsfreien Betrieb in Industriebehältern.",
    points: ["Kontinuierliches V-Spaltprofil mit Zweipunktkontakt verhindert Partikeleinklemmung und ermöglicht schnelle Rückspülung.", "Vollverschweißte Schmelzschweißung widersteht zyklischen Druckstößen und Temperaturschwankungen ohne Medienverlust.", "Lieferbar mit BSP/NPT-Gewindestutzen, Spreizhülsen oder Flanschringen passend zu jeder Behälterbodenstärke."],
    facts: [{
      label: "Spaltöffnung",
      value: "0,05 – 2,0 mm"
    }, {
      label: "Werkstoffe",
      value: "AISI 304 / 316L"
    }, {
      label: "Gewindegrößen",
      value: '1/2" – 2" BSP/NPT'
    }, {
      label: "Strömung",
      value: "Direkt & revers"
    }],
    imageAlt: "MutualFilter Präzisions-Filterdüsen und Siebkörbe aus Edelstahl"
  },
  fr: {
    eyebrow: "Contrôle précis des fluides et rétention des milieux",
    title: "Crépines et buses de filtration",
    lead: "Crépines et buses de filtration sur mesure en fil en coin et toile d'acier inoxydable conçues pour une collecte homogène, une faible perte de charge et un fonctionnement anti-colmatage en cuves industrielles.",
    points: ["Profil de fente continu en V assurant un contact en deux points qui évite le coincement des particules et facilite le rétro-lavage.", "Construction mécano-soudée par fusion résistant aux coups de bélier et aux variations thermiques sans fuite de média.", "Configurables avec tiges filetées BSP/NPT, manchons extensibles ou collerettes sur mesure adaptées à chaque plancher."],
    facts: [{
      label: "Ouverture de fente",
      value: "0,05 – 2,0 mm"
    }, {
      label: "Alliages",
      value: "AISI 304 / 316L"
    }, {
      label: "Raccordements",
      value: '1/2" – 2" BSP/NPT'
    }, {
      label: "Sens de flux",
      value: "Direct et inverse"
    }],
    imageAlt: "Crépines et buses de filtration de précision en acier inoxydable MutualFilter"
  },
  pt: {
    eyebrow: "Controle preciso de fluidos e retenção de meios",
    title: "Bocais e crepinas de filtragem",
    lead: "Bocais e crepinas de filtragem sob medida em fio cunha e malha de aço inoxidável, projetados para coleta uniforme de líquidos, baixa perda de carga e operação sem entupimento em vasos industriais.",
    points: ["Perfil de fenda contínua em V com contato em dois pontos, prevenindo o entupimento de partículas e facilitando a retrolavagem.", "Construção totalmente soldada por fusão que resiste a golpes de aríete cíclicos e oscilações térmicas sem perda de leito.", "Disponíveis com hastes roscadas padrão BSP/NPT, buchas expansíveis ou colarinhos sob medida para qualquer espessura de placa."],
    facts: [{
      label: "Abertura de fenda",
      value: "0,05 – 2,0 mm"
    }, {
      label: "Ligas de aço",
      value: "AISI 304 / 316L"
    }, {
      label: "Padrões de rosca",
      value: '1/2" – 2" BSP/NPT'
    }, {
      label: "Direção do fluxo",
      value: "Direto e reverso"
    }],
    imageAlt: "Bocais e crepinas de filtragem de precisão em aço inoxidável MutualFilter"
  }
};
export default function NozzlesGroup() {
  const {
    lang
  } = useLanguage();
  const copy = pick(lang, copyData);
  return <VariantPanel id="filter-nozzles" eyebrow={copy.eyebrow} title={copy.title} lead={copy.lead} points={copy.points} facts={copy.facts} image={{
    src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-product-photograph-of-stain-bbbfab.png",
    alt: copy.imageAlt
  }} tone="background" href="/FilterNozzlesPage" />;
}